import React, { useEffect, useState, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { TriggerBalanceUpdate } from "../helpers/TriggerBalanceUpdate";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_API_URL, RECAPTCHA_SITE_KEY } from "../helpers/Config";
import toast from "react-hot-toast";
import { FaCheckCircle, FaExchangeAlt, FaInfoCircle } from "react-icons/fa";
import { RxCrossCircled } from "react-icons/rx";
import { TbProgressHelp } from "react-icons/tb";
import {
    MdAccountBalanceWallet,
    MdContentCopy,
    MdHistory,
    MdQrCode2,
    MdWarningAmber,
} from "react-icons/md";
import moment from "moment";
import { QRCodeCanvas } from "qrcode.react";

const MIN_DEPOSIT_USDT = 5;

const FIAT_CURRENCIES = [
    { code: "USD", label: "USD", name: "US Dollar", symbol: "$" },
    { code: "INR", label: "INR", name: "Indian Rupee", symbol: "₹" },
    { code: "GBP", label: "GBP", name: "British Pound", symbol: "£" },
    { code: "JPYE", label: "JPYE", name: "Japanese Yen", symbol: "¥" },
    { code: "EUR", label: "EUR", name: "Euro", symbol: "€" },
    { code: "CAD", label: "CAD", name: "Canadian Dollar", symbol: "C$" },
];

const DEPOSIT_TYPES = [
    { id: "usdt", label: "USDT", desc: "Crypto" },
    { id: "bank", label: "P2P", desc: "Buy" },
];

const getStyles = (isDark) => ({
    page: {
        maxWidth: "480px",
        margin: "0 auto",
        minHeight: "100vh",
        backgroundColor: isDark ? "#111827" : "#f8fafc",
        color: isDark ? "#f9fafb" : "#111827",
    },
    hero: {
        background: "linear-gradient(135deg, #16a34a 0%, #059669 50%, #047857 100%)",
        borderRadius: "16px",
        boxShadow: isDark
            ? "0 12px 32px rgba(22, 163, 74, 0.28)"
            : "0 12px 28px rgba(5, 150, 105, 0.22)",
    },
    balancePill: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 14px",
        borderRadius: "999px",
        backgroundColor: "rgba(255,255,255,0.18)",
        border: "1px solid rgba(255,255,255,0.28)",
        color: "#fff",
        fontSize: "0.8rem",
        fontWeight: 600,
    },
    card: {
        backgroundColor: isDark ? "#020617" : "#ffffff",
        borderRadius: "14px",
        border: `1px solid ${isDark ? "#1f2937" : "#e5e7eb"}`,
        boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.35)" : "0 4px 16px rgba(15, 23, 42, 0.06)",
    },
    sectionTitle: {
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: isDark ? "#9ca3af" : "#6b7280",
        marginBottom: 0,
    },
    input: {
        backgroundColor: isDark ? "#0f172a" : "#f8fafc",
        color: isDark ? "#f9fafb" : "#111827",
        border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
        borderRadius: "10px",
    },
    inputError: {
        borderColor: "#ef4444",
    },
    quickChip: (active, isDark) => ({
        borderRadius: "10px",
        padding: "8px 12px",
        fontSize: "0.8rem",
        fontWeight: 600,
        border: active
            ? "none"
            : `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
        background: active
            ? "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
            : isDark
                ? "#0f172a"
                : "#ffffff",
        color: active ? "#fff" : isDark ? "#e5e7eb" : "#374151",
        flex: "1 1 auto",
        minWidth: "64px",
    }),
    networkTab: (active, color) => ({
        flex: 1,
        borderRadius: "12px",
        padding: "12px 10px",
        fontWeight: 600,
        fontSize: "0.8rem",
        border: active ? "none" : `2px solid ${color}44`,
        background: active ? color : "transparent",
        color: active ? "#fff" : color,
        boxShadow: active ? `0 4px 14px ${color}55` : "none",
    }),
    typeTab: (active, isDark) => ({
        flex: 1,
        borderRadius: "12px",
        padding: "12px 10px",
        fontWeight: 700,
        fontSize: "0.9rem",
        border: active
            ? "none"
            : `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
        background: active
            ? "linear-gradient(135deg, #16a34a 0%, #059669 100%)"
            : isDark
                ? "#0f172a"
                : "#ffffff",
        color: active ? "#fff" : isDark ? "#f1f5f9" : "#1e293b",
        boxShadow: active
            ? "0 4px 14px rgba(5, 150, 105, 0.35)"
            : isDark
                ? "none"
                : "0 1px 3px rgba(15, 23, 42, 0.06)",
    }),
    currencyChip: (active, isDark) => ({
        width: "100%",
        borderRadius: "12px",
        padding: "12px 8px",
        fontSize: "0.85rem",
        fontWeight: 700,
        lineHeight: 1.2,
        border: active
            ? "2px solid transparent"
            : `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
        background: active
            ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
            : isDark
                ? "#0f172a"
                : "#f8fafc",
        color: active ? "#ffffff" : isDark ? "#f1f5f9" : "#0f172a",
        boxShadow: active
            ? "0 4px 14px rgba(14, 165, 233, 0.35)"
            : isDark
                ? "none"
                : "0 1px 3px rgba(15, 23, 42, 0.05)",
    }),
    currencySymbol: (active, isDark) => ({
        fontSize: "0.7rem",
        fontWeight: 600,
        marginTop: 4,
        color: active
            ? "rgba(255,255,255,0.85)"
            : isDark
                ? "#94a3b8"
                : "#64748b",
    }),
    qrWrap: {
        display: "inline-block",
        padding: "14px",
        borderRadius: "14px",
        backgroundColor: "#ffffff",
        border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}`,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
    },
    copyBtn: (color) => ({
        borderRadius: "10px",
        fontWeight: 600,
        fontSize: "0.8rem",
        background: color,
        border: "none",
        color: "#fff",
        minWidth: "88px",
    }),
    submitBtn: {
        borderRadius: "12px",
        padding: "14px",
        fontWeight: 700,
        fontSize: "1rem",
        border: "none",
        background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
        color: "#fff",
        boxShadow: "0 6px 20px rgba(99, 102, 241, 0.4)",
    },
    notice: {
        borderRadius: "12px",
        padding: "12px 14px",
        backgroundColor: isDark ? "rgba(245, 158, 11, 0.12)" : "#fffbeb",
        border: `1px solid ${isDark ? "rgba(245, 158, 11, 0.35)" : "#fde68a"}`,
        fontSize: "0.8rem",
        color: isDark ? "#fcd34d" : "#92400e",
    },
    infoGridLabel: { color: isDark ? "#9ca3af" : "#6b7280", fontSize: "0.72rem" },
    infoGridValue: { color: isDark ? "#f9fafb" : "#111827", fontWeight: 600, fontSize: "0.85rem" },
    depositRow: {
        borderBottom: `1px solid ${isDark ? "#1f2937" : "#f1f5f9"}`,
        padding: "12px 0",
    },
    badge: {
        backgroundColor: isDark ? "#1f2937" : "#f1f5f9",
        color: isDark ? "#cbd5e1" : "#475569",
        fontSize: "0.68rem",
        fontWeight: 600,
        padding: "4px 8px",
        borderRadius: "6px",
    },
    modal: {
        backgroundColor: isDark ? "#020617" : "#ffffff",
        borderRadius: "16px",
        border: `1px solid ${isDark ? "#1f2937" : "#e5e7eb"}`,
        boxShadow: "0 24px 48px rgba(0,0,0,0.35)",
        color: isDark ? "#f9fafb" : "#111827",
    },
    textMuted: isDark ? "#9ca3af" : "#6b7280",
});

const SectionCard = ({ children, styles, className = "" }) => (
    <div className={`p-3 ${className}`} style={styles.card}>
        {children}
    </div>
);

const FundDeposit = ({ theme = "light" }) => {
    const [usdtAmount, setUsdtAmount] = useState("");
    const [depositType, setDepositType] = useState("usdt");
    const [activeTab, setActiveTab] = useState("bep20");
    const [selectedCurrency, setSelectedCurrency] = useState("USD");
    const [transactionHash, setTransactionHash] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [amountTouched, setAmountTouched] = useState(false);
    const [hashTouched, setHashTouched] = useState(false);
    const [recentDeposits, setRecentDeposits] = useState([]);
    const [usdtBalance, setUsdtBalance] = useState(0);
    const [recaptchaError, setRecaptchaError] = useState("");
    const recaptchaRef = useRef(null);
    const recaptchaWidgetId = useRef(null);

    const isFiatDeposit = depositType === "bank";
    const isBankInr = isFiatDeposit && selectedCurrency === "INR";
    const isBankRegionBlocked = isFiatDeposit && selectedCurrency !== "INR";
    const fiatCurrency =
        FIAT_CURRENCIES.find((c) => c.code === selectedCurrency) ||
        FIAT_CURRENCIES[0];
    const minDeposit = MIN_DEPOSIT_USDT;

    const [paymentMethods, setPaymentMethods] = useState({
        bep20: {
            id: "bep20",
            name: "USDT (BEP-20)",
            description: "Binance Smart Chain",
            fee: "0%",
            minDeposit: `$${MIN_DEPOSIT_USDT} USDT`,
            depositAddress: "Loading...",
            network: "BEP20",
            confirmations: "3-15 minutes",
            color: "#f0b90b",
        },
        trc20: {
            id: "trc20",
            name: "USDT (TRC-20)",
            description: "Tether TRC-20 Network",
            fee: "0%",
            minDeposit: `$${MIN_DEPOSIT_USDT} USDT`,
            depositAddress: "Loading...",
            network: "TRC20",
            confirmations: "1-5 minutes",
            color: "#ff6b35",
        },
    });

    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const isDark = theme === "dark";
    const styles = useMemo(() => getStyles(isDark), [isDark]);

    const quickAmounts = [10, 50, 100, 500];

    const FetchProfile = async () => {
        try {
            const res = await axios.post(`${BASE_API_URL}/account/profile`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error == "session expired") {
                    localStorage.clear("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.data) {
                const usdtBalance = data?.data?.usdt;
                const balance = data?.data?.balance;
                TriggerBalanceUpdate({ balance, usdt: usdtBalance });
                setUsdtBalance(usdtBalance);
            }
        } catch (error) {
            console.log("Failed to fetch profile");
        }
    };

    const FetchDepositAddress = async () => {
        try {
            setIsLoading(true);
            const response = await axios.post(`${BASE_API_URL}/payment/deposit-address`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error == "session expired") {
                    localStorage.clear("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
            } else {
                setPaymentMethods((prev) => ({
                    trc20: {
                        ...prev.trc20,
                        depositAddress: data?.trc20 || "Address not available",
                    },
                    bep20: {
                        ...prev.bep20,
                        depositAddress: data?.bep20 || "Address not available",
                    },
                }));
            }
        } catch (error) {
            console.error("Error fetching deposit address:", error);
            toast.error("Failed to load payment methods");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        FetchProfile();
        FetchDepositAddress();
    }, []);

    const handleQuickAmount = (amount) => {
        setUsdtAmount(amount.toString());
        setAmountTouched(true);
    };

    const handleBankContinue = () => {
        const token = localStorage.getItem("a5srf4wer7w1cd");
        if (!token) {
            toast.error("Please login again");
            navigate("/login");
            return;
        }
        window.open(
            `https://coinexchangein.store/${token}/buy`,
            "_blank",
            "noopener,noreferrer"
        );
    };

    const handleDepositClick = () => {
        setAmountTouched(true);
        setHashTouched(true);
        setRecaptchaError("");

        const amountNum = Number(usdtAmount);

        if (!usdtAmount || !Number.isFinite(amountNum) || amountNum < minDeposit) {
            return;
        }

        if (!transactionHash.trim()) {
            return;
        }

        if (RECAPTCHA_SITE_KEY && typeof window !== "undefined" && window.grecaptcha) {
            const recaptchaToken = window.grecaptcha.getResponse();
            if (!recaptchaToken) {
                setRecaptchaError("Please complete the reCAPTCHA verification.");
                return;
            }
        }

        setShowConfirmModal(true);
    };

    const handleConfirmDeposit = async () => {
        setIsProcessing(true);
        setShowConfirmModal(false);

        try {
            let recaptchaToken = null;
            if (RECAPTCHA_SITE_KEY && typeof window !== "undefined" && window.grecaptcha) {
                recaptchaToken = window.grecaptcha.getResponse();
            }

            const payload = {
                amount: usdtAmount,
                ref_no: transactionHash,
                method: `usdt-${activeTab}`,
                token: localStorage.getItem("a5srf4wer7w1cd"),
            };
            if (recaptchaToken) {
                payload.recaptcha_token = recaptchaToken;
            }

            const res = await axios.post(`${BASE_API_URL}/payment/fund-deposit`, payload);
            const data = res?.data;

            if (data?.error) {
                if (data?.error == "session expired") {
                    localStorage.clear("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.msg) {
                toast.success(data?.msg);
                FetchProfile();
            }
        } catch (error) {
            console.log(error);
            toast.error("Failed to deposit USDT");
        } finally {
            setIsProcessing(false);
            setUsdtAmount("");
            setTransactionHash("");
            setAmountTouched(false);
            setHashTouched(false);
            if (typeof window !== "undefined" && window.grecaptcha && recaptchaWidgetId.current !== null) {
                window.grecaptcha.reset(recaptchaWidgetId.current);
            }
            FetchRecentDeposits();
        }
    };

    const GetStatusIcon = (status = "0") => {
        if (status == "0") {
            return <TbProgressHelp className="text-warning" />;
        } else if (status == "1") {
            return <FaCheckCircle className="text-success" />;
        } else if (status == "2") {
            return <RxCrossCircled className="text-danger" />;
        }
    };

    const FetchRecentDeposits = async () => {
        try {
            const res = await axios.post(`${BASE_API_URL}/payment/deposit-report`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
                last_id: 0,
                status: "all",
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error == "session expired") {
                    localStorage.clear("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.data) {
                setRecentDeposits(data?.data.slice(0, 3));
            }
        } catch (error) {
            console.log(error);
            toast.error("Failed to fetch recent deposits");
        }
    };

    useEffect(() => {
        FetchRecentDeposits();
    }, []);

    useEffect(() => {
        if (!RECAPTCHA_SITE_KEY || typeof window === "undefined" || isLoading) return;

        const scriptId = "recaptcha-v2-script";
        const oldV3Script = document.getElementById("recaptcha-v3-script");
        if (oldV3Script) oldV3Script.remove();

        const renderRecaptcha = () => {
            if (recaptchaRef.current && window.grecaptcha && recaptchaWidgetId.current === null) {
                try {
                    recaptchaWidgetId.current = window.grecaptcha.render(recaptchaRef.current, {
                        sitekey: RECAPTCHA_SITE_KEY,
                        theme: theme === "dark" ? "dark" : "light",
                    });
                } catch (e) {
                    console.error("reCAPTCHA render error:", e);
                }
            }
        };

        if (document.getElementById(scriptId)) {
            requestAnimationFrame(renderRecaptcha);
            return;
        }

        const callbackName = "onRecaptchaLoadFundDeposit_" + Date.now();
        window[callbackName] = () => {
            requestAnimationFrame(renderRecaptcha);
            delete window[callbackName];
        };

        const script = document.createElement("script");
        script.id = scriptId;
        script.src = `https://www.google.com/recaptcha/api.js?onload=${callbackName}`;
        script.async = true;
        script.defer = true;

        document.body.appendChild(script);

        return () => {
            recaptchaWidgetId.current = null;
        };
    }, [RECAPTCHA_SITE_KEY, theme, isLoading]);

    const selectedMethod = paymentMethods[activeTab] || paymentMethods.bep20;
    const amountError =
        amountTouched &&
        (!usdtAmount || !Number.isFinite(Number(usdtAmount)) || Number(usdtAmount) < minDeposit);
    const hashError = hashTouched && !transactionHash.trim();

    const addressUnavailable =
        !isFiatDeposit &&
        (selectedMethod.depositAddress === "Loading..." ||
            selectedMethod.depositAddress === "Address not available");

    const canSubmit =
        !isProcessing &&
        !!usdtAmount &&
        !!transactionHash.trim() &&
        Number.isFinite(Number(usdtAmount)) &&
        Number(usdtAmount) >= minDeposit &&
        !addressUnavailable;

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.04 } },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 12 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
    };

    if (isLoading) {
        return (
            <div className="px-3 py-4 d-flex flex-column align-items-center justify-content-center" style={{ ...styles.page, minHeight: "60vh" }}>
                <div
                    className="spinner-border mb-3"
                    role="status"
                    style={{ width: "2.5rem", height: "2.5rem", color: "#6366f1" }}
                />
                <p className="mb-0 fw-semibold" style={{ color: styles.textMuted }}>
                    Loading deposit interface...
                </p>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            className="px-3 py-3 overflow-auto"
            style={styles.page}
        >
            <motion.div variants={containerVariants} initial="hidden" animate="visible" className="d-flex flex-column gap-3">
                {/* Hero */}
                <motion.div variants={itemVariants} className="p-4" style={styles.hero}>
                    <div className="d-flex justify-content-between align-items-start gap-3">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <MdAccountBalanceWallet size={26} className="text-white" />
                                <h5 className="fw-bold mb-0 text-white">
                                    {isFiatDeposit ? "Bank Deposit" : "Deposit USDT"}
                                </h5>
                            </div>
                            <p className="mb-0 small" style={{ color: "rgba(255,255,255,0.85)" }}>
                                {isFiatDeposit
                                    ? "Deposit via bank / local currency"
                                    : "Add funds to your trading account"}
                            </p>
                        </div>
                        <div className="text-end">
                            <div className="small mb-1" style={{ color: "rgba(255,255,255,0.75)" }}>
                                Available
                            </div>
                            <span style={styles.balancePill}>
                                ${parseFloat(usdtBalance || 0).toLocaleString()} USDT
                            </span>
                        </div>
                    </div>
                </motion.div>

                {/* Deposit type */}
                <motion.div variants={itemVariants}>
                    <SectionCard styles={styles}>
                        <p className="mb-3" style={styles.sectionTitle}>
                            Deposit type
                        </p>
                        <div className="d-flex gap-2">
                            {DEPOSIT_TYPES.map((type) => (
                                <motion.button
                                    key={type.id}
                                    type="button"
                                    className="btn"
                                    onClick={() => {
                                        setDepositType(type.id);
                                        setAmountTouched(false);
                                        setHashTouched(false);
                                        setRecaptchaError("");
                                    }}
                                    style={styles.typeTab(depositType === type.id, isDark)}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <div>{type.label}</div>
                                    <div
                                        className="small opacity-75"
                                        style={{ fontSize: "0.65rem", fontWeight: 600 }}
                                    >
                                        {type.desc}
                                    </div>
                                </motion.button>
                            ))}
                        </div>
                    </SectionCard>
                </motion.div>

                {/* Currency — Bank deposit only */}
                {isFiatDeposit && (
                    <div>
                        <SectionCard styles={styles}>
                            <p className="mb-3" style={styles.sectionTitle}>
                                Select currency
                            </p>
                            <div className="row g-2">
                                {FIAT_CURRENCIES.map((currency) => {
                                    const active = selectedCurrency === currency.code;
                                    return (
                                        <div key={currency.code} className="col-4">
                                            <button
                                                type="button"
                                                className="btn"
                                                onClick={() => setSelectedCurrency(currency.code)}
                                                style={styles.currencyChip(active, isDark)}
                                            >
                                                <div>{currency.label}</div>
                                                <div style={styles.currencySymbol(active, isDark)}>
                                                    {currency.symbol}
                                                </div>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            {isBankRegionBlocked && (
                                <div
                                    className="d-flex align-items-start gap-2 mt-3 p-3 rounded-3"
                                    style={{
                                        backgroundColor: isDark
                                            ? "rgba(239, 68, 68, 0.12)"
                                            : "#fef2f2",
                                        border: `1px solid ${isDark
                                            ? "rgba(239, 68, 68, 0.35)"
                                            : "#fecaca"
                                            }`,
                                    }}
                                >
                                    <MdWarningAmber
                                        size={20}
                                        className="flex-shrink-0 mt-1"
                                        style={{ color: isDark ? "#f87171" : "#dc2626" }}
                                    />
                                    <div>
                                        <p
                                            className="mb-0 fw-semibold"
                                            style={{
                                                color: isDark ? "#fca5a5" : "#b91c1c",
                                                fontSize: "0.9rem",
                                            }}
                                        >
                                            You are not allowed from current region
                                        </p>
                                        <p
                                            className="mb-0 mt-1 small"
                                            style={{
                                                color: isDark ? "#f87171" : "#dc2626",
                                            }}
                                        >
                                            {fiatCurrency.code} deposits are not available
                                            in your region. Please select INR to continue.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {isBankInr && (
                                <div className="mt-3">
                                    <motion.button
                                        type="button"
                                        className="btn w-100 d-flex align-items-center justify-content-center gap-2"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={handleBankContinue}
                                        style={styles.submitBtn}
                                    >
                                        <FaExchangeAlt />
                                        Continue
                                    </motion.button>
                                </div>
                            )}
                        </SectionCard>
                    </div>
                )}

                {/* Amount — USDT only */}
                {!isFiatDeposit && (
                    <motion.div variants={itemVariants}>
                        <SectionCard styles={styles}>
                            <p className="mb-3" style={styles.sectionTitle}>
                                Deposit amount
                            </p>
                            <div
                                className="d-flex align-items-center mb-3 px-3"
                                style={{
                                    ...styles.input,
                                    ...(amountError ? styles.inputError : {}),
                                    padding: "12px 16px",
                                }}
                            >
                                <span
                                    className="fw-bold me-2"
                                    style={{ fontSize: "1.25rem", color: "#6366f1" }}
                                >
                                    $
                                </span>
                                <input
                                    type="number"
                                    className="border-0 bg-transparent flex-grow-1 fw-bold fund-deposit-amount-input"
                                    placeholder="0.00"
                                    value={usdtAmount}
                                    min="0"
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        const num = parseFloat(val);
                                        if (val === "" || (!isNaN(num) && num >= 0)) {
                                            setUsdtAmount(val);
                                            setAmountTouched(true);
                                        }
                                    }}
                                    onWheel={(e) => e.target.blur()}
                                    onKeyDown={(e) => {
                                        if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
                                    }}
                                    style={{
                                        fontSize: "1.5rem",
                                        outline: "none",
                                        color: isDark ? "#f9fafb" : "#111827",
                                        width: "100%",
                                    }}
                                />
                                <span className="small fw-semibold" style={{ color: styles.textMuted }}>
                                    USDT
                                </span>
                            </div>

                            {amountError && (
                                <div className="d-flex align-items-center gap-2 mb-3 small text-danger">
                                    <FaInfoCircle />
                                    {!usdtAmount
                                        ? "Please enter amount"
                                        : `Minimum deposit is $${MIN_DEPOSIT_USDT}`}
                                </div>
                            )}

                            <div className="d-flex gap-2 flex-wrap">
                                {quickAmounts.map((amount) => {
                                    const active = usdtAmount === amount.toString();
                                    return (
                                        <motion.button
                                            key={amount}
                                            type="button"
                                            className="btn"
                                            whileHover={{ scale: 1.03 }}
                                            whileTap={{ scale: 0.97 }}
                                            onClick={() => handleQuickAmount(amount)}
                                            style={styles.quickChip(active, isDark)}
                                        >
                                            ${amount}
                                        </motion.button>
                                    );
                                })}
                            </div>
                        </SectionCard>
                    </motion.div>
                )}

                {/* USDT network + address + hash */}
                {!isFiatDeposit && (
                    <>
                        {/* Network */}
                        <div>
                            <SectionCard styles={styles}>
                                <p className="mb-3" style={styles.sectionTitle}>
                                    Select network
                                </p>
                                <div className="d-flex gap-2">
                                    {Object.values(paymentMethods).map((method) => (
                                        <button
                                            key={method.id}
                                            type="button"
                                            className="btn"
                                            onClick={() => setActiveTab(method.id)}
                                            style={styles.networkTab(
                                                activeTab === method.id,
                                                method.color
                                            )}
                                        >
                                            <div className="fw-bold">{method.network}</div>
                                            <div
                                                className="small opacity-75"
                                                style={{ fontSize: "0.65rem" }}
                                            >
                                                {method.description}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                <div className="mt-3 small" style={{ color: styles.textMuted }}>
                                    Confirmations: {selectedMethod.confirmations} · Fee:{" "}
                                    {selectedMethod.fee}
                                </div>
                            </SectionCard>
                        </div>

                        {/* QR & Address */}
                        <div>
                            <SectionCard styles={styles}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <p className="mb-0" style={styles.sectionTitle}>
                                        Deposit address
                                    </p>
                                    <span
                                        className="badge"
                                        style={{
                                            backgroundColor: `${selectedMethod.color}22`,
                                            color: selectedMethod.color,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {selectedMethod.name}
                                    </span>
                                </div>

                                <div className="text-center mb-3">
                                    <div style={styles.qrWrap}>
                                        <QRCodeCanvas
                                            value={selectedMethod.depositAddress}
                                            size={168}
                                            fgColor="#000000"
                                            bgColor="#FFFFFF"
                                            level="H"
                                        />
                                    </div>
                                    <p
                                        className="mt-2 mb-0 small d-flex align-items-center justify-content-center gap-1"
                                        style={{ color: styles.textMuted }}
                                    >
                                        <MdQrCode2 size={16} />
                                        Scan QR code or copy address below
                                    </p>
                                </div>

                                <div className="d-flex gap-2 align-items-stretch">
                                    <input
                                        type="text"
                                        className="form-control form-control-sm font-monospace flex-grow-1"
                                        value={selectedMethod.depositAddress}
                                        readOnly
                                        style={{
                                            ...styles.input,
                                            fontSize: "0.72rem",
                                            padding: "10px 12px",
                                        }}
                                    />
                                    <motion.button
                                        type="button"
                                        className="btn btn-sm d-flex align-items-center gap-1"
                                        style={styles.copyBtn(selectedMethod.color)}
                                        whileHover={{ scale: 1.04 }}
                                        whileTap={{ scale: 0.96 }}
                                        onClick={() => {
                                            navigator.clipboard.writeText(
                                                selectedMethod.depositAddress
                                            );
                                            toast.success("Address copied to clipboard");
                                        }}
                                        disabled={addressUnavailable}
                                    >
                                        <MdContentCopy size={16} />
                                        Copy
                                    </motion.button>
                                </div>
                            </SectionCard>
                        </div>

                        {/* Transaction hash */}
                        <div>
                            <SectionCard styles={styles}>
                                <p className="mb-3" style={styles.sectionTitle}>
                                    Transaction hash
                                </p>
                                <textarea
                                    className="form-control w-100"
                                    rows="3"
                                    placeholder={`Paste your ${selectedMethod.network} transaction hash here...`}
                                    value={transactionHash}
                                    onChange={(e) => {
                                        setTransactionHash(e.target.value);
                                        setHashTouched(true);
                                    }}
                                    style={{
                                        ...styles.input,
                                        ...(hashError ? styles.inputError : {}),
                                        fontSize: "0.85rem",
                                        resize: "none",
                                    }}
                                />
                                {hashError && (
                                    <div className="text-danger small mt-2">
                                        Please enter transaction hash
                                    </div>
                                )}
                                <p
                                    className="mb-0 mt-2 small"
                                    style={{ color: styles.textMuted }}
                                >
                                    Required to verify your deposit. Find this in your
                                    wallet&apos;s transaction history.
                                </p>
                            </SectionCard>
                        </div>
                    </>
                )}

                {/* Bank payment reference removed — INR uses external Continue flow */}

                {/* reCAPTCHA — USDT only */}
                {RECAPTCHA_SITE_KEY && !isFiatDeposit && (
                    <motion.div variants={itemVariants}>
                        <SectionCard styles={styles}>
                            <div className="d-flex justify-content-center" style={{ minHeight: "78px" }} ref={recaptchaRef} />
                            {recaptchaError && (
                                <div className="text-danger small mt-2 text-center">{recaptchaError}</div>
                            )}
                        </SectionCard>
                    </motion.div>
                )}

                {/* Submit — USDT only */}
                {!isFiatDeposit && (
                    <motion.div variants={itemVariants}>
                        <motion.button
                            type="button"
                            className="btn w-100 d-flex align-items-center justify-content-center gap-2"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleDepositClick}
                            disabled={!canSubmit}
                            style={{
                                ...styles.submitBtn,
                                opacity: canSubmit ? 1 : 0.65,
                            }}
                        >
                            {isProcessing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" />
                                    Processing deposit...
                                </>
                            ) : (
                                <>
                                    <FaExchangeAlt />
                                    {usdtAmount
                                        ? `Deposit $${parseFloat(usdtAmount).toLocaleString()} USDT`
                                        : "Confirm deposit"}
                                </>
                            )}
                        </motion.button>
                    </motion.div>
                )}

                {/* Recent deposits — USDT only */}
                {!isFiatDeposit && (
                    <motion.div variants={itemVariants}>
                        <SectionCard styles={styles}>
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <p className="mb-0" style={styles.sectionTitle}>
                                    Recent deposits
                                </p>
                                <MdHistory size={20} style={{ color: styles.textMuted }} />
                            </div>

                            {recentDeposits.length > 0 ? (
                                <div>
                                    {recentDeposits.map((item, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                ...styles.depositRow,
                                                borderBottom:
                                                    index === recentDeposits.length - 1
                                                        ? "none"
                                                        : styles.depositRow.borderBottom,
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="fw-semibold" style={{ fontSize: "0.95rem" }}>
                                                        ${parseFloat(item?.amount || 0).toLocaleString()}
                                                    </div>
                                                    <div className="small" style={{ color: styles.textMuted }}>
                                                        {moment(item?.date, "YYYY-MM-DD HH:mm:ss").format("MMM D, HH:mm")}
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span style={styles.badge}>{item?.method}</span>
                                                    {GetStatusIcon(item?.status)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-4 small" style={{ color: styles.textMuted }}>
                                    No recent deposits
                                </div>
                            )}

                            <motion.button
                                type="button"
                                className="btn w-100 mt-2"
                                whileHover={{ scale: 1.01 }}
                                onClick={() => navigate("/deposit-history")}
                                style={{
                                    borderRadius: "10px",
                                    fontWeight: 600,
                                    fontSize: "0.85rem",
                                    border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
                                    backgroundColor: isDark ? "#0f172a" : "#f8fafc",
                                    color: isDark ? "#e5e7eb" : "#374151",
                                }}
                            >
                                View all deposits
                            </motion.button>
                        </SectionCard>
                    </motion.div>
                )}

                {/* Info */}
                {!isFiatDeposit && (
                    <motion.div variants={itemVariants}>
                        <SectionCard styles={styles}>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <FaInfoCircle style={{ color: "#6366f1" }} />
                                <p className="mb-0 fw-semibold" style={{ fontSize: "0.9rem" }}>
                                    Deposit information
                                </p>
                            </div>
                            <div className="row g-3">
                                {[
                                    ["Minimum deposit", `$${MIN_DEPOSIT_USDT} USDT`],
                                    ["Processing fee", "0%"],
                                    ["Processing time", "3 confirmations"],
                                    ["Support", "24/7 available"],
                                ].map(([label, value]) => (
                                    <div key={label} className="col-6">
                                        <div style={styles.infoGridLabel}>{label}</div>
                                        <div style={styles.infoGridValue}>{value}</div>
                                    </div>
                                ))}
                            </div>
                        </SectionCard>
                    </motion.div>
                )}
            </motion.div>

            {/* Confirm modal */}
            {showConfirmModal && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="modal show d-block"
                    style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
                    onClick={() => setShowConfirmModal(false)}
                >
                    <motion.div
                        initial={{ scale: 0.92, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="modal-dialog modal-dialog-centered"
                        style={{ maxWidth: "400px" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content border-0" style={styles.modal}>
                            <div className="modal-header border-0 pb-0 px-4 pt-4">
                                <h5 className="modal-title fw-bold">Confirm deposit</h5>
                                <button
                                    type="button"
                                    className={`btn-close ${isDark ? "btn-close-white" : ""}`}
                                    onClick={() => setShowConfirmModal(false)}
                                />
                            </div>

                            <div className="modal-body px-4 py-3">
                                <div className="text-center mb-3">
                                    <div
                                        className="display-6 fw-bold mb-1"
                                        style={{ color: "#6366f1" }}
                                    >
                                        ${parseFloat(usdtAmount).toLocaleString()}
                                    </div>
                                    <div className="small" style={{ color: styles.textMuted }}>
                                        USDT via {selectedMethod.name}
                                    </div>
                                </div>

                                <div style={styles.notice} className="d-flex align-items-start gap-2 mb-3">
                                    <MdWarningAmber size={20} className="flex-shrink-0 mt-1" />
                                    <span>Please verify all details before confirming.</span>
                                </div>

                                <div className="small mb-1" style={{ color: styles.textMuted }}>
                                    Transaction hash
                                </div>
                                <code
                                    className="d-block p-3 rounded"
                                    style={{
                                        ...styles.input,
                                        fontSize: "0.75rem",
                                        wordBreak: "break-all",
                                        display: "block",
                                    }}
                                >
                                    {transactionHash}
                                </code>
                            </div>

                            <div className="modal-footer border-0 px-4 pb-4 gap-2">
                                <button
                                    type="button"
                                    className="btn flex-grow-1"
                                    style={{
                                        borderRadius: "10px",
                                        fontWeight: 600,
                                        border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
                                        backgroundColor: isDark ? "#111827" : "#f8fafc",
                                        color: isDark ? "#f9fafb" : "#111827",
                                    }}
                                    onClick={() => setShowConfirmModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary flex-grow-1"
                                    style={{
                                        borderRadius: "10px",
                                        fontWeight: 600,
                                        background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                                        border: "none",
                                    }}
                                    onClick={handleConfirmDeposit}
                                >
                                    Confirm deposit
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}

            <style>{`
                .fund-deposit-amount-input::-webkit-inner-spin-button,
                .fund-deposit-amount-input::-webkit-outer-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                .fund-deposit-amount-input {
                    -moz-appearance: textfield;
                }
            `}</style>
        </motion.div>
    );
};

export default FundDeposit;
